import React, { useState, useEffect } from 'react';
import { GlobalMicroButton } from '../../GlobalMicroButton';
import { StatusBadge } from '../../StatusBadge';
import styles from './URSCard.module.css';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useTheme } from '../../../context/ThemeProvider';
import { UserRequirement, RequirementStatus } from '../../../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

export type Requirement = UserRequirement;

interface URSCardProps {
  requirement: Requirement;
  projectId: string;
  onEdit?: (requirement: Requirement) => void;
  onDelete?: (requirementId: string) => void;
  onAiEnhance?: (requirement: Requirement) => void;
  onViewTraceability?: (requirement: Requirement) => void;
  onGenerateFromURS?: (requirement: Requirement) => void;
  onComplete?: (requirement: Requirement) => void;
  onEntityClick?: (entityId: string, entityType: 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
  compact?: boolean;
}

export const URSCard: React.FC<URSCardProps> = ({
  requirement,
  projectId,
  onEdit,
  onDelete,
  onAiEnhance,
  onViewTraceability,
  onGenerateFromURS,
  onComplete,
  onEntityClick,
  compact = false
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [entityTitles, setEntityTitles] = useState<Record<string, string>>({});
  const [reverseLinkedEntities, setReverseLinkedEntities] = useState<{
    fs_ids: string[];
    test_ids: string[];
    risk_ids: string[];
    sop_ids: string[];
  }>({
    fs_ids: [],
    test_ids: [],
    risk_ids: [],
    sop_ids: []
  });

  // Fetch entity titles for connected entities (both forward and reverse)
  useEffect(() => {
    const fetchEntityTitles = async () => {
      if (!projectId || !isExpanded) return;

      const titles: Record<string, string> = {};
      const reverseLinks = {
        fs_ids: [] as string[],
        test_ids: [] as string[],
        risk_ids: [] as string[],
        sop_ids: [] as string[]
      };

      // Fetch FS documents - both forward links and reverse links
      try {
        const fsCol = collection(db, 'projects', projectId, 'fs');
        const fsSnapshot = await getDocs(fsCol);
        fsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `FS-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const ursIds = links.urs_ids || [];

          // If this FS references our URS, add it to reverse links
          if (ursIds.includes(requirement.id)) {
            reverseLinks.fs_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this FS is in our forward links, add its title
          if (requirement.links.fs_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching FS titles:', err);
        }
      }

      // Fetch TEST documents - both forward links and reverse links
      try {
        const testCol = collection(db, 'projects', projectId, 'tests');
        const testSnapshot = await getDocs(testCol);
        testSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const ursIds = links.urs_ids || [];

          // If this TEST references our URS, add it to reverse links
          if (ursIds.includes(requirement.id)) {
            reverseLinks.test_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this TEST is in our forward links, add its title
          if (requirement.links.test_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching TEST titles:', err);
        }
      }

      // Fetch RISK documents - both forward links and reverse links
      try {
        const riskCol = collection(db, 'projects', projectId, 'risks');
        const riskSnapshot = await getDocs(riskCol);
        riskSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const ursIds = links.urs_ids || [];

          // If this RISK references our URS, add it to reverse links
          if (ursIds.includes(requirement.id)) {
            reverseLinks.risk_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this RISK is in our forward links, add its title
          if (requirement.links.risk_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching RISK titles:', err);
        }
      }

      // Fetch SOP documents - both forward links and reverse links
      try {
        const sopCol = collection(db, 'projects', projectId, 'sops');
        const sopSnapshot = await getDocs(sopCol);
        sopSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const ursIds = links.urs_ids || [];

          // If this SOP references our URS, add it to reverse links
          if (ursIds.includes(requirement.id)) {
            reverseLinks.sop_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this SOP is in our forward links, add its title
          if (requirement.links.sop_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching SOP titles:', err);
        }
      }

      setEntityTitles(titles);
      setReverseLinkedEntities(reverseLinks);
    };

    fetchEntityTitles();
  }, [projectId, requirement.id, requirement.links, isExpanded]);

  const getStatusBadge = (status: RequirementStatus | string) => {
    const statusMap: Record<string, { textKey: string; variant: 'success' | 'warning' | 'error' | 'info' }> = {
      approved: { textKey: 'global_status_approved', variant: 'success' },
      in_review: { textKey: 'global_status_in_review', variant: 'warning' },
      draft: { textKey: 'global_status_draft', variant: 'info' },
      retired: { textKey: 'global_status_rejected', variant: 'error' }
    };
    const key = status.toString().toLowerCase().replace(' ', '_');
    const statusInfo = statusMap[key] || { textKey: status.toString(), variant: 'info' as const };
    return <StatusBadge text={t(statusInfo.textKey)} variant={statusInfo.variant} size="medium" />;
  };

  const formatDate = (date: any) => {
    try {
      return new Date(date?.seconds ? date.seconds * 1000 : date).toLocaleDateString();
    } catch {
      return '';
    }
  };

  if (compact) {
    return (
      <div className={styles.requirementCardCompact}>
        <div className={styles.compactHeader}>
          <span className={styles.reqId}>{requirement.id}</span>
          {getStatusBadge(requirement.status)}
        </div>
        <p className={styles.compactDescription}>{requirement.description}</p>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on action buttons, links, or card actions
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.cardActions}`)) {
      return;
    }
    if (target.closest('button') && !target.closest(`.${styles.cardHeader}`)) {
      return;
    }
    if (target.closest(`.${styles.entityLinkItem}`)) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      id={`entity-${requirement.id}`}
      data-entity-card
      className={styles.requirementCard}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <h4 className={styles.reqId}>
            {requirement.id}: {requirement.title}
          </h4>
        </div>
        <div className={styles.headerRight}>
          {getStatusBadge(requirement.status)}
          <div
            data-expand-trigger
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          >
            <img
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"}
              alt={isExpanded ? "Collapse" : "Expand"}
              width={12}
              height={12}
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <p className={styles.description}>{requirement.description}</p>

      <div className={`${styles.requirementDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.requirementDetailsInner}>

          {/* Inline meta fields */}
          <div className={styles.inlineMeta}>

            {/* Rationale */}
            {requirement.rationale && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_rationale')}:</span>
                <span className={styles.metaValue}> {requirement.rationale}</span>
              </div>
            )}

            {/* Category */}
            {requirement.category && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_category')}:</span>
                <span className={styles.metaValue}> {requirement.category}</span>
              </div>
            )}

            {/* GXP Relevance */}
            {requirement.gxp_relevance && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_gxp_relevance')}:</span>
                <span className={styles.metaValue}> {requirement.gxp_relevance}</span>
              </div>
            )}

            {/* Business Priority */}
            {requirement.business_priority && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_business_priority')}:</span>
                <span className={styles.metaValue}> {requirement.business_priority}</span>
              </div>
            )}

            {/* Owner 
            {requirement.meta?.owner && (
              <div>
                <span className={styles.metaValue}>{requirement.meta.owner}</span>
              </div>
            )}*/}
          </div>

          {/* Dates on separate line */}
          {(requirement.createdAt || requirement.updatedAt) && (
            <div className={styles.inlineMeta}>
              {requirement.createdAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_created')}:</span>
                  <span className={styles.metaValue}> {formatDate(requirement.createdAt)}</span>
                </div>
              )}
              {requirement.updatedAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_updated')}:</span>
                  <span className={styles.metaValue}> {formatDate(requirement.updatedAt)}</span>
                </div>
              )}
            </div>
          )}

          {/* Connected Entities - combine forward and reverse links */}
          {(() => {
            // Combine forward and reverse links, removing duplicates
            const allFsIds = Array.from(new Set([...requirement.links.fs_ids, ...reverseLinkedEntities.fs_ids]));
            const allTestIds = Array.from(new Set([...requirement.links.test_ids, ...reverseLinkedEntities.test_ids]));
            const allRiskIds = Array.from(new Set([...requirement.links.risk_ids, ...reverseLinkedEntities.risk_ids]));
            const allSopIds = Array.from(new Set([...requirement.links.sop_ids, ...reverseLinkedEntities.sop_ids]));

            const hasAnyLinks = allFsIds.length > 0 || allTestIds.length > 0 || allRiskIds.length > 0 || allSopIds.length > 0;

            if (!hasAnyLinks) return null;

            return (
              <div className={styles.connectedEntitiesSection}>
                {allFsIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_fs') || 'Linked FS'}:</span>
                    <div className={styles.entityList}>
                      {allFsIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'FS');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allTestIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_tests') || 'Linked TEST'}:</span>
                    <div className={styles.entityList}>
                      {allTestIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'TEST');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allRiskIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_risks') || 'Linked RISK'}:</span>
                    <div className={styles.entityList}>
                      {allRiskIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'RISK');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allSopIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_sops') || 'Linked SOP'}:</span>
                    <div className={styles.entityList}>
                      {allSopIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'SOP');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Card actions */}
          <div
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
          >

            {/* Edit button */}
            {onEdit && (
              <GlobalMicroButton
                onClick={() => onEdit(requirement)}
                icon={<img src={isDarkMode ? "/assets/icons/pencil.svg" : "/assets/icons/pencil.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_edit')}
              </GlobalMicroButton>
            )}

            {/* Delete button */}
            {onDelete && (
              <GlobalMicroButton
                onClick={() => onDelete(requirement.id)}
                icon={<img src={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_delete')}
              </GlobalMicroButton>
            )}

            {/* Generate from URS button */}
            {onGenerateFromURS && (
              <GlobalMicroButton
                onClick={() => onGenerateFromURS(requirement)}
                icon={<img src={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"} alt="" width={12} height={12} />}
              >
                {t('project_tab_ai_generate_from_urs') || 'Mit KI generieren'}
              </GlobalMicroButton>
            )}

            {/* Complete button */}
            {onComplete && (
              <GlobalMicroButton
                onClick={() => onComplete(requirement)}
                icon={<img src={isDarkMode ? "/assets/icons/checkmark.square.svg" : "/assets/icons/checkmark.square.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_complete') || 'Complete'}
              </GlobalMicroButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

